/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useMemo, useEffect, useCallback } from 'react';
// redux
import { useSelector, useDispatch } from 'react-redux';
import { PRODUCT_COMMENTING, checkCommentingEmailIfExist, setCommentingAccountStatus} from '../../../../../../state/thunks/account';
// utils
import { validation as userValidations } from '../../../../../../utils/validation';
import { formatRoleName } from '../utils/userutils';
// material
import { Title, Form, Grid, GridSet, GridItem, TextField, CheckboxField, Switch, ListSelectDropDown, displayModal, Copy, CTAs, Button, log } from '@insticator/insticator-ui'; // iui-material
import ExpandableSection from "../../../../../../material/expandablesection/expandablesection";
import ConfigController from "../../../../../../material/expandablesection/controllers/configcontroller/configcontroller";
import SectionCard from "../../../../../../material/expandablesection/sectioncard/sectioncard";
import SectionGroup from '../../../../../../material/expandablesection/sectiongroup/sectiongroup';
import SectionGroupDescription from '../../../../../../material/expandablesection/sectiongroupdescription/sectiongroupdescription';
// styles
import injectSheet from 'react-jss';
import styles from './userform_styles';
import { InlineMessage, SingleSelectDropDown } from '@insticator/insticator-ui/dist/material';

const formName = 'userdetailsform'
const existingCommentingAccountMessage = `
  There is already a commenting account associated with this email address.
  Saving this user will merge the two accounts. All existing comments on the 
  existing account will now have the new username, commenting role badge 
  and avatar.
`

const validation = {...userValidations, email: userValidations.userEmail}
/* ========== ~~~~~~~~~~ ACCOUNT USER FORM ~~~~~~~~~~ ========== */
const UserForm = props => {

  const dispatch = useDispatch()

  // redux states
  const { sites, accountProducts, accountCommentingRoles, updatingUsersError, accountUsers } = useSelector(state => state.account);
  const hasCommenting = accountProducts?.includes(PRODUCT_COMMENTING)

  const { title, data, isProcessing, onSave, onCancel, onResendInvite, isResendingInvite, addingUserError } = props;
  const isEdit = !!data.userUUID

  const [isOpened, setIsOpened] = useState(true);
  // use for showing custom email validations
  const [isEmailChanged, setIsEmailChanged] = useState(false) 
  // flag for new user if form is submitted and if fields can be validated
  const [isNewUserSubmitted, setIsNewUserSubmitted] = useState(false)
  
  const [isEditUserSubmitted, setIsEditUserSubmitted] = useState(false)

  // commenting user account
  const [commentingUser] = useState(data?.commentingUser || null)
  const [isCommentingAccountOn, setIsCommentingAccountOn] = useState(commentingUser ? !commentingUser.deactivated : false)
  const commentingRoles = useMemo(() => accountCommentingRoles.map(role => ({name: formatRoleName(role.roleName), value: role.uuid, icon: role.badgeImageURL})), [accountCommentingRoles])
  const [selectedCommentingRole, selectCommentingRole] = useState(commentingRoles.find(role => role.value === data.commentingUser?.roleUUID) || null)
  const [commentingUsername, setCommentingUsername] = useState(data?.commentingUser?.username || '')
  const [commentingUsernameChanged, setCommentingUsernameChanged] = useState(false)
  const [selectedSites, selectSites] = useState(data?.commentingUser?.allowedSiteUUIDs || [])
  const [isCommentingUserChanged, setCommentingUserChanged] = useState(false)
  const [isCommentingEmailExist, setIsCommentingEmailExist] = useState(false)
  const [isEmailFilledAfterToggledOn, setIsEmailFilledAfterToggledOn] = useState(false)
  const [isCheckingCommentingEmail, checkingCommentingEmail] = useState(false)

  // transpiled user data
  const [userData] = useState({
    firstName: data?.firstName || '',
    lastName: data?.lastName || '',
    email: data?.email || '',
    phoneNumber: data?.phoneNumber || '',
    userUUID: data?.userUUID || null,
    permissions: data?.permissions || [],
    accountUUID: data?.accountUUID || null
  })
  // user fields
  const [userFields, setUserFields] = useState({
    fname: userData.firstName || '',
    lname: userData.lastName || '',
    email: userData.email || '',
    permissions: userData.permissions
  })
  // initial user fields values
  const [initialFieldsState, setInitialFieldsState] = useState({...userFields})

  useEffect(() => {
    // set initial fields when passed user data got updated
    setInitialFieldsState({
      fname: data?.firstName || '',
      lname: data?.lastName || '',
      email: data?.email || '',
      permissions: data?.permissions || []
    })
  }, [data])

  const userHasPermission = permission => userFields.permissions.includes(permission)

  const isValid = name => validation[name].test.test(userFields[name])

  const isFormValid = useMemo(() => isValid(`fname`) && isValid(`lname`) && isValid(`email`),[userFields])

  // custom commenting username validation
  const isCommentingUsernameValid = () => validation[`commentingUsername`].test.test(commentingUsername)

  const isCommentingFormValid = useMemo(() => {
    const isFormValid = isCommentingUsernameValid() && !!selectedCommentingRole && selectedSites.length > 0
    return isCommentingAccountOn ? isFormValid : true
  }, [selectedCommentingRole, commentingUsername, isCommentingAccountOn, selectedSites])

  const handleToggle = () => setIsOpened(!isOpened)

  const handleSave = () => {
    if(!isEdit) setIsNewUserSubmitted(true)
    else setIsEditUserSubmitted(true)
    let newData = {
      data: {
        firstName: userFields.fname,
        lastName: userFields.lname,
        email: userFields.email,
        permissions: userFields.permissions,
        phoneNumber: userData.phoneNumber,
        commentingUser: isCommentingAccountOn &&  isCommentingFormValid ? {
          roleUUID: selectedCommentingRole.value,
          username: commentingUsername,
          deactivated: false,
          allowedSiteUUIDs: selectedSites 
        } : null
      }
    }

    if(userData.accountUUID) newData.accountUUID = userData.accountUUID
    if(userData.userUUID) newData.userUUID = userData.userUUID
    if(isFormValid && isCommentingFormValid) {
      setIsEmailChanged(false)
      setCommentingUserChanged(false)

      // activate/deactivate commenting account
      if(isEdit && commentingUser) {
        if(isCommentingAccountOn && commentingUser.deactivated === true) {
          // activate commenting user
          log('ACTIVATE commenting user', userFields.email)
          dispatch(setCommentingAccountStatus({userUUID: userData.userUUID, isDeactivated: false}))
        }
        if(!isCommentingAccountOn && commentingUser.deactivated === false) {
          // deactivate commenting user
          log('DEACTIVATE commenting user', userFields.email)
          dispatch(setCommentingAccountStatus({userUUID: userData.userUUID, isDeactivated: true}))
        }
      }
      onSave(newData)
    }
  }

  const handleCancel = () => {
    setUserFields(initialFieldsState)
    if(onCancel) onCancel()
  }

  const handleFieldsChange = (e, fieldName) => setUserFields({...userFields, [fieldName]: e.target.value})

  const handlePermissionsChange = (permission) => {
    const permissionFields = [...userFields.permissions]
    const idx = permissionFields.indexOf(permission);
    idx < 0 ? permissionFields.push(permission) : permissionFields.splice(idx, 1);
    const newPermissions = {
      ...userFields,
      permissions: permissionFields
    }
    setUserFields(newPermissions)
  }

  const isChanged = useMemo(() => {
    const isFieldsChanged = !(JSON.stringify(initialFieldsState) === JSON.stringify(userFields));
    return isFieldsChanged
  }, [userFields, initialFieldsState])

  const controller = (
    <ConfigController
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={() =>  isEmailFilledAfterToggledOn && isFormValid && isCommentingFormValid ? handleExistingEmailPopup() : handleSave()}
      isOpened={isOpened}
      isChanged={isChanged}
      isProcessing={isProcessing}
      disableSave={!isFormValid}
      title={title}
    />
  );

  // custom email validation 
  const emailAlreadyExistInAccount = useMemo(() => accountUsers.filter(user => user.email === userFields.email).length > 0, [userFields.email])
  const showExistingEmailError = addingUserError && addingUserError.includes('email') && isValid('email') && !isEmailChanged && !isProcessing
  const isEmailFieldValid = isValid(`email`) && !emailAlreadyExistInAccount && (!(addingUserError && addingUserError.includes('email')) || isEmailChanged)

  // duplicate username validation
  const updateUserErrorIndex = updatingUsersError.map(usersErr => usersErr.userUUID).indexOf(userData.userUUID)
  const showDuplicateCommentingUsernameError = 
    (updateUserErrorIndex >= 0 && updatingUsersError[updateUserErrorIndex]?.error?.includes('username') || (addingUserError && addingUserError.includes('username')))
    && isCommentingUsernameValid && !isCommentingUserChanged && !isProcessing

  const handleExistingEmailPopup = useCallback(async ()=> {
    const isExist = await checkCommentingEmail();
    if(isExist) {
      dispatch(displayModal(true, 
        <div name={`existingemail`} style={{maxWidth: '600px'}}>
        <Copy name={`emailexist`}>
          <InlineMessage
            name={'emailexistinlinemsg'}
            variant={'system'}
            icon={'exclamation-triangle'}
            message={existingCommentingAccountMessage}
            close={false}
          />
        </Copy>
        <CTAs name={`emailexistctas`}>
            <Button type={`action`} handleClick={() => dispatch(displayModal(false))} variant={`normal`} name={`cancel`} text={`Cancel`} />
            <Button type={`action`} handleClick={() => {dispatch(displayModal(false)); handleSave()}} variant={'primary'} name={'save'} text={'Save & Close'} />
        </CTAs>
    </div>)) 
    } else {
      handleSave()
    }
  }, [userFields, isCommentingFormValid]);

  const checkCommentingEmail = useCallback(async () => {
    checkingCommentingEmail(true)
    if(userFields.email) {
      const response = await dispatch(checkCommentingEmailIfExist(userFields.email))
      const isExist = response?.payload?.isExist || false
      setIsCommentingEmailExist(isExist)
      checkingCommentingEmail(false)
      return isExist;
    }
  }, [userFields, isCommentingFormValid]);

  const currentRole = useMemo(() => {
    let role = {}
    if(selectedCommentingRole) role = accountCommentingRoles.find(role => role.uuid === selectedCommentingRole.value)
    else role = null
    return role
  }, [selectedCommentingRole])

  const commentingUserNameErrorEl = (
    <div class={'errormessage'}>
    {commentingUsername.length < 2 ? `Username must be between 2 and 16 characters` : validation[`commentingUsername`].message}
    </div>
  )

  const validateCommentingUsername = () => {
    if(isEdit) {
      if(isEditUserSubmitted || (isCommentingAccountOn && isCommentingUserChanged)) return isCommentingUsernameValid()
    } else {
      if(isNewUserSubmitted) return isCommentingUsernameValid()
    }
    return true
  }

  const commentingRoleErrorEl = (
    <div className={'errormessage'}>Select a commenting role for this user</div>
  )

  const validateCommentingRole = () => {
    if(isEdit) {
      if(isEditUserSubmitted) return !!selectedCommentingRole
    } else {
      if(isNewUserSubmitted) return !!selectedCommentingRole
    }
    return true
  }

  const validateCommentingSites = () => {
    if(isEdit) {
      if(isEditUserSubmitted) return selectedSites.length > 0
    } else {
      if(isNewUserSubmitted) return selectedSites.length > 0
    }
    return true
  }

  const commentingSitesErrorEl = (
    <div class={'errormessage'}>Select atleast one site </div>
  )

  const showCommentingEmailError = isCommentingEmailExist && isValid('email') && !isCheckingCommentingEmail && !isEmailFilledAfterToggledOn && !isEdit

  const deactivateCommentingAccountMessage = !isCommentingAccountOn && isEdit && commentingUser && !commentingUser.deactivated &&
    <InlineMessage
      variant={'neutral'}
      icon={'exclamation-triangle'}
      message={`On save this user's account will be disabled. They will not be able to log in, but their historical comments will still be displayed on any sites`}
      close={false}
      name={'disable-commenting-account-message'}
    />

  const deactivatedCommentingAccountMessage = !isCommentingAccountOn && isEdit && commentingUser && commentingUser.deactivated &&
    <InlineMessage
      variant={'neutral'}
      icon={'exclamation-triangle'}
      message={`Commenting account is currently disabled. Historical comments will still be displayed on any sites but user will not be able to login this commenting account. `}
      close={false}
      name={'disable-commenting-account-message'}
    />

  const activateCommentingAccountMessage = isCommentingAccountOn && isEdit && commentingUser && commentingUser.deactivated &&
    <InlineMessage
      variant={'neutral'}
      icon={'exclamation-triangle'}
      message={`On save this user's account will be activated again.`}
      close={false}
      name={'activate-commenting-account-message'}
    />

  const disableCommentingFields = (!isCommentingAccountOn && !!commentingUser) || !!commentingUser?.deactivated

  return (
    <ExpandableSection
      className={`userform ${props.classes.userform} ${!isEdit && 'newuserform'}`}
      controller={isEdit ? controller : null}
      isOpened={isOpened}
      isChanged={isEdit ? (isChanged || isCommentingUserChanged) : true}
      isProcessing={isProcessing}
      disableSave={isEdit ? !isFormValid : false}
      handleCancel={handleCancel}
      handleSave={() => isEmailFilledAfterToggledOn && isFormValid && isCommentingFormValid ? handleExistingEmailPopup() : handleSave()}
    >
      {isOpened && <>
      <Form name={formName} editable={true} processing={isProcessing}>
        <SectionCard>
          {!isEdit && <Title klass={'newusertitle'}><h2>New User</h2></Title>}
            <Grid direction={`vertical`} name={`formcontainer`}>
                <GridSet direction={`horizontal`} name={'details'}>
                  <GridItem>
                    <TextField 
                      type={`text`} 
                      formName={formName} 
                      name={`fname`} 
                      value={userFields.fname} 
                      handleChange={e => handleFieldsChange(e, `fname`)} 
                      valid={isEdit ? isValid(`fname`) : (isNewUserSubmitted ? isValid(`fname`) : true)} 
                      placeholder={`Enter your First Name`} 
                      label={`First Name`} 
                      required 
                    />
                  </GridItem>
                  <GridItem>
                    <TextField 
                      type={`text`} 
                      formName={formName} 
                      name={`lname`} 
                      value={userFields.lname} 
                      handleChange={e => handleFieldsChange(e, `lname`)} 
                      valid={isEdit ? isValid(`lname`) : (isNewUserSubmitted ? isValid(`lname`) : true)} 
                      placeholder={`Enter your Last Name`} 
                      label={`Last Name`} 
                      required 
                    />
                  </GridItem>
                  <GridItem>
                    <TextField 
                      klass={'emailfield'}
                      type={`text`}
                      formName={formName} 
                      name={`emailfield`} 
                      value={userFields.email} 
                      handleChange={e => {
                        setIsEmailChanged(true)
                        checkingCommentingEmail(true)
                        handleFieldsChange(e, `email`)
                      }} 
                      handleBlur={e => (validation['email'].test.test(e.target.value) && isCommentingAccountOn && !isEmailFilledAfterToggledOn) && checkCommentingEmail()}
                      valid={isEdit ? isEmailFieldValid : (isNewUserSubmitted ? isEmailFieldValid : !emailAlreadyExistInAccount)} 
                      placeholder={`Enter your Email`} 
                      label={`Email`} 
                      validateOnChange 
                      disabled={isEdit} 
                      required
                    />
                    {emailAlreadyExistInAccount && !isEdit && <div class={`errormessage`}>This email has already been used in this account.</div>}
                    {showExistingEmailError && !emailAlreadyExistInAccount && <div class={'errormessage'}>{addingUserError}</div>}
                    {isEdit ? !isValid('email') : (isNewUserSubmitted ? !isValid('email') : false) && <div class={'errormessage'}>Please enter a valid email</div>}
                  </GridItem>
                </GridSet>
                <label className={'permissionslabel'}>Permissions</label>
                <GridSet direction={`horizontal`} name={'permissions'}>
                  <GridSet direction={'vertical'}>
                    <GridItem>
                      <CheckboxField 
                        type={`checkbox`} 
                        formName={formName} 
                        name={`edit_payment`} 
                        value={userHasPermission('settings.paymentInfo')} 
                        checked={userHasPermission('settings.paymentInfo')} 
                        valid
                        label={`Edit Payment Information`} 
                        handleChange={() => handlePermissionsChange('settings.paymentInfo')} 
                      />
                    </GridItem>
                    <GridItem>
                      <CheckboxField 
                        type={`checkbox`} 
                        formName={formName} 
                        name={`change_permissions`} 
                        value={userHasPermission('settings.users')} 
                        checked={userHasPermission('settings.users')} 
                        valid
                        label={`Change user permissions`} 
                        handleChange={() => handlePermissionsChange('settings.users')} 
                      />
                    </GridItem>
                    <GridItem>
                      <CheckboxField 
                        type={`checkbox`} 
                        formName={formName} 
                        name={`manage_sites`} 
                        value={userHasPermission('settings.sites')}
                        checked={userHasPermission('settings.sites')} 
                        valid
                        label={`Manage sites`} 
                        handleChange={() => handlePermissionsChange('settings.sites')} 
                      />
                    </GridItem>
                    <GridItem>
                      <CheckboxField 
                        type={`checkbox`} 
                        formName={formName} 
                        name={`account_information`} 
                        value={userHasPermission('settings.general')} 
                        checked={userHasPermission('settings.general')} 
                        valid 
                        label={`Edit account information`} 
                        handleChange={() => handlePermissionsChange('settings.general')} 
                      />
                    </GridItem>
                  </GridSet>
                  <GridSet direction={'vertical'}>
                    <GridItem>
                      <CheckboxField 
                        type={`checkbox`} 
                        formName={formName} 
                        name={`adstxt`} 
                        value={userHasPermission('settings.adsTxt')} 
                        checked={userHasPermission('settings.adsTxt')}
                        valid
                        label={`Manage Ads.txt file`} 
                        handleChange={() => handlePermissionsChange('settings.adsTxt')} 
                      />
                    </GridItem>
                    <GridItem>
                      <CheckboxField 
                        type={`checkbox`} 
                        formName={formName} 
                        name={`create_embeds`} 
                        value={userHasPermission('manageEmbeds')} 
                        checked={userHasPermission('manageEmbeds')} 
                        valid
                        label={`Create, edit, and view embeds`} 
                        handleChange={() => handlePermissionsChange('manageEmbeds')} 
                      />
                    </GridItem>
                    <GridItem>
                      <CheckboxField 
                        type={`checkbox`} 
                        formName={formName} 
                        name={`home_adMetrics`} 
                        value={userHasPermission('home.adMetrics')} 
                        checked={userHasPermission('home.adMetrics')} 
                        valid
                        label={`Embed Analytics - Ad Metrics`} 
                        handleChange={() => handlePermissionsChange('home.adMetrics')} 
                      />
                    </GridItem>
                  </GridSet>
                </GridSet>
            </Grid>
            {/* TODO: Hide this Resend Email Invite for now. Moving to backlog for Pub User Badges */}
            {/* {isEdit && 
              <Button 
                type={'action'} 
                variant={'primary'} 
                name={'resendinvite'} 
                text={'Resend Email Invite'} 
                operation={'send'}
                action={onResendInvite} 
                processing={isResendingInvite}
              />
            } */}
          </SectionCard>
        </Form>
      </>}
    </ExpandableSection>
  );
};

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default injectSheet(styles)(UserForm);
